import { SvgIconComponent } from '@mui/icons-material';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import CrisisAlertOutlinedIcon from '@mui/icons-material/CrisisAlertOutlined';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import DoubleArrowOutlinedIcon from '@mui/icons-material/DoubleArrowOutlined';
import DriveEtaOutlinedIcon from '@mui/icons-material/DriveEtaOutlined';
import DynamicFormOutlinedIcon from '@mui/icons-material/DynamicFormOutlined';
import EmojiObjectsOutlinedIcon from '@mui/icons-material/EmojiObjectsOutlined';
import ExtensionOutlinedIcon from '@mui/icons-material/ExtensionOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import InterestsOutlinedIcon from '@mui/icons-material/InterestsOutlined';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';
import PersonPinOutlinedIcon from '@mui/icons-material/PersonPinOutlined';
import ReceiptOutlinedIcon from '@mui/icons-material/ReceiptOutlined';
import RouteOutlinedIcon from '@mui/icons-material/RouteOutlined';
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';
import SellOutlinedIcon from '@mui/icons-material/SellOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import WorkspacesOutlinedIcon from '@mui/icons-material/WorkspacesOutlined';

import resourceNames from '../../constants/resourceNames';

export type PageContext = {
  label: string;
  icon: SvgIconComponent;
};

type PageName = {
  list: string;
  // Only resources with a record or create route have a singular.
  singular?: string;
  icon: SvgIconComponent;
};

const RULE_REVIEWS = `${resourceNames.RULES}/${resourceNames.REVIEWS}`;
const ACCESS_MANAGEMENT = 'access-management';
const SECURITY = 'security';

// Labels follow the sidebar (menuGroups.tsx and options.label in app.tsx),
// icons are the outlined versions of the sidebar icons. See chips.md 7.27.
const pageNames: Record<string, PageName> = {
  [resourceNames.CASES]: { list: 'Cases', singular: 'case', icon: DescriptionOutlinedIcon },
  [resourceNames.PINGS]: { list: 'Pings', singular: 'ping', icon: EmojiObjectsOutlinedIcon },
  [resourceNames.REQUESTS]: { list: 'Requests', singular: 'request', icon: DoubleArrowOutlinedIcon },
  [resourceNames.REPORTS]: { list: 'Reports', icon: AssessmentOutlinedIcon },
  [resourceNames.ACCOUNT_ENTITIES]: { list: 'Accounts', singular: 'account', icon: AccountBalanceOutlinedIcon },
  [resourceNames.BUSINESSES]: { list: 'Businesses', singular: 'business', icon: BusinessOutlinedIcon },
  [resourceNames.INDIVIDUALS]: { list: 'Individuals', singular: 'individual', icon: PersonPinOutlinedIcon },
  [resourceNames.PRODUCTS]: { list: 'Products', singular: 'product', icon: ExtensionOutlinedIcon },
  [resourceNames.CARS]: { list: 'Cars', singular: 'car', icon: DriveEtaOutlinedIcon },
  [resourceNames.RISK_CLASSES]: { list: 'Risk classes', icon: CrisisAlertOutlinedIcon },
  [resourceNames.GROUPS]: { list: 'Groups', icon: WorkspacesOutlinedIcon },
  [resourceNames.TRANSACTION_TYPES]: { list: 'Transaction types', icon: ReceiptOutlinedIcon },
  [resourceNames.PROCESSES]: { list: 'Processes', icon: RouteOutlinedIcon },
  [resourceNames.SIGNALS]: { list: 'Signals', icon: NotificationsOutlinedIcon },
  [resourceNames.RULES]: { list: 'Rules', singular: 'rule', icon: TuneOutlinedIcon },
  [RULE_REVIEWS]: { list: 'Rule reviews', icon: TuneOutlinedIcon },
  [ACCESS_MANAGEMENT]: { list: 'Access management', icon: PeopleOutlinedIcon },
  [resourceNames.AUDIT_TRACKS]: { list: 'Audit trail', icon: HistoryOutlinedIcon },
  [resourceNames.TAGS]: { list: 'Tags', singular: 'tag', icon: SellOutlinedIcon },
  [resourceNames.FORMS]: { list: 'Forms', singular: 'form', icon: DynamicFormOutlinedIcon },
  [resourceNames.INTERFACE_SETTINGS]: { list: 'Interface settings', icon: InterestsOutlinedIcon },
  [SECURITY]: { list: 'Security', icon: SecurityOutlinedIcon },
};

// Resources with a create route in app.tsx. For the others, react-admin
// matches "create" to the record route as a record id.
const withCreateRoute = new Set<string>([
  resourceNames.BUSINESSES,
  resourceNames.CARS,
  resourceNames.INDIVIDUALS,
  resourceNames.PRODUCTS,
  resourceNames.REQUESTS,
  resourceNames.RULES,
  resourceNames.TAGS,
]);

const DASHBOARD: PageContext = { label: 'Dashboard', icon: DashboardOutlinedIcon };
const FALLBACK: PageContext = { label: 'This page', icon: ArticleOutlinedIcon };

// Names the page in words, never by a path segment or a record id.
const getPageContext = (pathname: string): PageContext => {
  const segments = pathname.split('/').filter(Boolean);
  if (!segments.length) return DASHBOARD;

  const [first, second, third] = segments;
  if (first === ACCESS_MANAGEMENT) {
    return { label: pageNames[ACCESS_MANAGEMENT].list, icon: pageNames[ACCESS_MANAGEMENT].icon };
  }

  const isRuleReviews = `${first}/${second}` === RULE_REVIEWS;
  const key = isRuleReviews ? RULE_REVIEWS : first;
  // pageNames is a plain object, so keys like "constructor" would match inherited values.
  if (!Object.hasOwn(pageNames, key)) return FALLBACK;
  const page = pageNames[key];

  const subpath = isRuleReviews ? third : second;
  if (!subpath) return { label: page.list, icon: page.icon };
  if (!page.singular) return FALLBACK;

  const isCreate = subpath === 'create' && withCreateRoute.has(key);
  const prefix = isCreate ? 'New' : 'This';
  return { label: `${prefix} ${page.singular}`, icon: page.icon };
};

export default getPageContext;
